import {
  ArticleHeading2,
  ArticleHeading3,
  ArticleParagraph1,
  ArticleParagraph2,
  ArticleParagraph3,
  ModalWindow,
} from '@cian/ui-kit';

import { type FC, type ReactNode } from 'react';

import { type IPromoBank, type IPromoFeature } from '../../types/promos';

import { AuthorItem } from './components/AuthorItem';
import { BankItem } from './components/BankItem';
import { getBankLogoUrl } from './helpers/getBankLogoUrl';

import styles from './PromoDetails.css';

export interface IPromoDetailsProps {
  authorDescription: string | null | undefined;
  authorName: string | null | undefined;
  authorLabel: string | null | undefined;
  authorLogoUrl: string | null | undefined;
  contactsButton: ReactNode | undefined;
  bank: IPromoBank | null | undefined;
  banks: IPromoBank[] | null | undefined;
  description: string | null | undefined;
  features: IPromoFeature[] | null | undefined;
  mortgagePartnerLink: string | null;
  onClose(): void;
  onMortgageClick(): void;
  open: boolean;
  subtitle?: string | null;
  title: string;
}

export const PromoDetails: FC<IPromoDetailsProps> = ({
  authorDescription,
  authorLabel,
  authorLogoUrl,
  authorName,
  bank,
  banks,
  contactsButton,
  description,
  features,
  mortgagePartnerLink,
  onClose,
  onMortgageClick,
  open,
  subtitle,
  title,
}) => {
  return (
    <ModalWindow escape outside fixed={false} maxWidth={688} open={open} width="100%" onClose={onClose}>
      <div className={styles['content']} data-testid="PromoDetailsModal">
        <div className={styles['title']}>
          <ArticleHeading2 as="h2">{title}</ArticleHeading2>
        </div>
        {subtitle && (
          <div className={styles['subtitle']}>
            <ArticleParagraph1>{subtitle}</ArticleParagraph1>
          </div>
        )}
        {features && !!features.length && (
          <div className={styles['features-list']}>
            {features.map(({ name, value }, idx) => (
              <div className={styles['feature']} key={idx}>
                <ArticleParagraph3 color="gray60_100">{name}</ArticleParagraph3>
                <ArticleParagraph1>{value}</ArticleParagraph1>
              </div>
            ))}
          </div>
        )}
        {description && (
          <div className={styles['description']}>
            <ArticleParagraph1 whiteSpace="pre-line">{description}</ArticleParagraph1>
          </div>
        )}
        {banks && !!banks.length && (
          <div className={styles['banks']}>
            <div className={styles['banks-title']}>
              <ArticleHeading3>Банки</ArticleHeading3>
            </div>
            <ul className={styles['banks-list']}>
              {banks.map(promoBank => (
                <li className={styles['bank-item']} key={promoBank.id}>
                  <BankItem
                    logoUrl={getBankLogoUrl(promoBank)}
                    mortgagePartnerLink={mortgagePartnerLink}
                    name={promoBank.name}
                    onMortgageClick={onMortgageClick}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
        {bank && (
          <div className={styles['banks']}>
            <BankItem
              label="Банк"
              logoUrl={getBankLogoUrl(bank)}
              mortgagePartnerLink={mortgagePartnerLink}
              name={bank.name}
              onMortgageClick={onMortgageClick}
            />
          </div>
        )}
        {authorName && (
          <>
            <AuthorItem contactsButton={contactsButton} label={authorLabel} logoUrl={authorLogoUrl} name={authorName} />
            {authorDescription && (
              <div className={styles['author-description']}>
                <ArticleParagraph2 color="gray60_100">{authorDescription}</ArticleParagraph2>
              </div>
            )}
          </>
        )}
      </div>
    </ModalWindow>
  );
};
